import { Component, Input, OnInit, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash';

import { SalesforceFieldService } from './salesforce-field/salesforce-field-control.service';
import { SalesforceErrorService } from '../salesforce-error/salesforce-error.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { SalesforceGroup, PrePopulateData } from '../salesforce-data.model';
import { SalesforceForm, SalesforceField, SalesforceError, SalesforceFormSection, SalesforceFieldTypes } from './model/salesforce-form.model';
import { SalesforceFormControl } from './model/salesforce-form-control';
import { BcRestApi } from 'common/api/rest-api';

@Component({
  selector: 'app-salesforce-form',
  templateUrl: './salesforce-form.component.html',
  styleUrls: ['./salesforce-form.component.scss'],
  providers: [SalesforceFieldService],
  encapsulation: ViewEncapsulation.None
})
export class SalesforceFormComponent implements OnInit {
  @Input()
  public group: SalesforceGroup;

  @Input()
  public chatId: string;

  @Input()
  public prePopulateData: PrePopulateData;

  @Output()
  public leaveCreate: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public saveAndLink: EventEmitter<any> = new EventEmitter<any>();

  form: UntypedFormGroup;
  fields: SalesforceField[];
  salesforceFormData: SalesforceForm;
  isLoading: boolean = false;
  isSavingSalesforceObject = false;
  fieldErrors: SalesforceError[];

  constructor(private _bcAnalytics: BcAnalytics,
              private _salesforceFieldService: SalesforceFieldService,
              private _bcRestApi: BcRestApi,
              private _salesforceErrorService: SalesforceErrorService) { }

  ngOnInit() {
    this.isLoading = true;
    this._salesforceFieldService.getSalesforceForm(this.chatId, this.group.id).then((data: SalesforceForm) => {
      this.salesforceFormData = this._prePopulateData(data);
      this.form = this._salesforceFieldService.toFormGroup(this.salesforceFormData);
      this.fields = this._getAllFields(this.salesforceFormData);
      this.isLoading = false;
      this._salesforceErrorService.clearError(this.chatId);
    }).catch((error) => {
      this.isLoading = false;
      this._bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
      this._salesforceErrorService.notifyError(this.chatId);
      this._closeForm();
    });
  }

  onSaveAndLinkClick(): void {
    this.isSavingSalesforceObject = true;
    const fieldValues = {};

    Object.keys(this.form.value).forEach(key => {
      fieldValues[key] = (<SalesforceFormControl> this.form.controls[key]).getApiValue();
    });
    this._salesforceErrorService.clearError(this.chatId);
    this._bcRestApi.createSalesforceObject(this.chatId, this.group.id, {fieldValues: fieldValues, hiddenFields: this.salesforceFormData.hiddenFields}).then(() => {
      this.saveAndLink.emit();
      this._bcAnalytics.eventTrack('Salesforce_SaveAndLink', { type: this.group.name });
    }).catch((error) => {
      this.isSavingSalesforceObject = false;
      if (error.data && error.data.code === 'FORM_VALIDATION_ERROR') {
        this.fieldErrors = error.data.fieldValidationErrors;

        const globalErrorText: string | undefined = this._getGlobalErrorText();
        if (globalErrorText) {
          this._salesforceErrorService.notifyError(this.chatId, globalErrorText);
        } else {
          this._salesforceErrorService.notifyError(this.chatId, 'salesforceForm.formErrorMessage');
        }
      } else {
        this._salesforceErrorService.notifyError(this.chatId);
      }

      this._bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
    });
  }

  getErrorText(field: SalesforceField): string | undefined {
    return (this.fieldErrors && this.fieldErrors.find(fieldError => fieldError.fieldLabel === field.label))
      ? this.fieldErrors.find((fieldError: SalesforceError) => fieldError.fieldLabel === field.label).fieldErrorMessage
      : undefined;
  }

  onCancelClick(): void {
    this._closeForm();
    this._salesforceErrorService.clearError(this.chatId);
  }

  private _getGlobalErrorText(): string | undefined {
    if (this.fieldErrors) {
      const globalErrors: SalesforceError[] = this.fieldErrors.filter((fieldError: SalesforceError) => {
        if (fieldError.fieldLabel === '') {
          return true;
        }

        const hasMatchingField: boolean = this.fields.filter((field: SalesforceField) => fieldError.fieldLabel === field.label).length > 0;
        return !hasMatchingField;
      });

      return globalErrors.length > 0
        ? globalErrors.map((fieldError: SalesforceError) => fieldError.rawErrorMessage).join(' ')
        : undefined;
    } else {
      return undefined;
    }
  }

  private _getAllFields(form: SalesforceForm): SalesforceField[] {
    let fields: SalesforceField[] = [];

    form.sections.forEach((section: SalesforceFormSection) => {
      fields = fields.concat(section.fields);
    });

    fields.forEach((field: SalesforceField) => {
      if (field.type !== SalesforceFieldTypes.PICKLIST) {
        return;
      }

      field.controlledPicklistValues = field.picklistValues;
    });

    return fields;
  }

  private _prePopulateData(data: SalesforceForm): SalesforceForm {
    const updatedDataObject: SalesforceForm = cloneDeep(data);
    if (this.prePopulateData) {
      updatedDataObject.sections.forEach(section => section.fields.forEach(field => {
        if (field && field.name === this.prePopulateData.property) {
          field.value = this.prePopulateData.data ;
        }
      }));
    }
    return updatedDataObject;
  }

  private _closeForm(): void {
    this.leaveCreate.emit();
  }
}
